import api from "@/services/api";
import {
  PLANIFICATIONS_BASE_URL,
  fetchPlanificationTicket,
  planificationApiFor,
  planificationBlobFor,
  planificationFetchHeaders,
} from "@/services/planificationApi";

// Fetch ticket, then run `fn(ticket)`. Keeps every offloaded call's pattern
// uniform: one ticket fetch + one cross-origin call.
function withTicket(scope, fn) {
  return fetchPlanificationTicket(scope).then((ticket) => fn(ticket));
}

export default {
  generatePlots(grade, type, genre, details) {
    const params = new URLSearchParams({
      grade: grade,
      type: type,
      genre: genre,
      details: details,
    }).toString();
    return withTicket("plots", (ticket) =>
      planificationApiFor(ticket)
        .get(`get_plots/?${params}`)
        .then((response) => response.data)
    );
  },
  generateCharacters(n, plot, type) {
    const params = new URLSearchParams({
      n: n,
      plot: plot,
      type: type,
    });
    return withTicket("characters", (ticket) =>
      planificationApiFor(ticket)
        .get(`get_characters/?${params}`)
        .then((response) => response.data)
    );
  },
  generateText(
    characters,
    plot,
    theme,
    nperson,
    type,
    details,
    age,
    style,
    resourceType
  ) {
    const params = new URLSearchParams({
      characters: characters,
      plot: plot,
      theme: theme,
      nperson: nperson,
      type: type,
      details: details,
      age: age,
      style: style,
      resource_type: resourceType,
    });
    return withTicket("text", (ticket) =>
      fetch(`${PLANIFICATIONS_BASE_URL}/get_text/?${params}`, {
        headers: planificationFetchHeaders(ticket),
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`get_text failed (${response.status})`);
        }
        return response.body;
      })
    );
  },
  saveText(payload) {
    return api.post("save_text/", payload).then((response) => response.data);
  },
  /**
   * Parse get_text stream (`[chunk];` + optional `<title>`).
   * Title markers can span TCP chunks; if GPT omits `<>`, the whole stream
   * is the body (do not drop it — that left Lenguaje fábula/novela empty).
   * Optional onProgress({ title, content }) for live UI streaming.
   */
  collectTextStream(body, { expectTitle = false, onProgress } = {}) {
    if (!body || typeof body.getReader !== "function") {
      return Promise.resolve({ title: "", content: "" });
    }
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let title = "";
    let content = "";
    let titleDone = !expectTitle;
    let pending = "";

    const stripBrackets = (raw) => {
      if (!raw) return "";
      if (raw.includes(";")) {
        return raw
          .split(";")
          .map((item) => {
            const s = item.trim();
            if (!s) return "";
            return s.startsWith("[") && s.endsWith("]")
              ? s.slice(1, -1)
              : s;
          })
          .join("");
      }
      const s = raw.trim();
      return s.startsWith("[") && s.endsWith("]") ? s.slice(1, -1) : s;
    };

    const titleFromInner = (inner) =>
      String(inner || "")
        .split(";")
        .map((item) => {
          const s = item.trim();
          if (!s) return "";
          return s.startsWith("[") && s.endsWith("]") ? s.slice(1, -1) : s;
        })
        .join("");

    const clean = (s) =>
      String(s || "")
        .replace(/^[\s[\]]+/, "")
        .replace(/[\s[\]]+$/, "")
        .trim();

    const emit = () => {
      if (typeof onProgress !== "function") return;
      const liveBody = titleDone
        ? content
        : content + stripBrackets(pending);
      onProgress({ title: clean(title), content: clean(liveBody) });
    };

    const pump = ({ done, value }) => {
      if (value) {
        const decoded = decoder.decode(value, { stream: true });
        if (!titleDone) {
          pending += decoded;
          const found = pending.match(/<([^>]*)>/);
          if (found) {
            title = titleFromInner(found[1]);
            const after = pending
              .slice(pending.indexOf(found[0]) + found[0].length)
              .replace(/^\n/, "");
            titleDone = true;
            pending = "";
            content += stripBrackets(after);
          } else if (pending.includes(">")) {
            // Marker closed across chunks without a clean <…> capture.
            const idx = pending.indexOf(">");
            title = titleFromInner(
              pending.slice(0, idx).replace(/^[\s\S]*</, "")
            );
            const after = pending.slice(idx + 1).replace(/^\n/, "");
            titleDone = true;
            pending = "";
            content += stripBrackets(after);
          }
        } else {
          content += stripBrackets(decoded);
        }
        emit();
      }
      if (done) {
        const tail = decoder.decode();
        if (tail) {
          if (!titleDone) pending += tail;
          else content += stripBrackets(tail);
        }
        if (!titleDone && pending) {
          // No title markers in the stream → body is the full text.
          content += stripBrackets(pending);
          pending = "";
        }
        const result = { title: clean(title), content: clean(content) };
        if (typeof onProgress === "function") onProgress(result);
        return result;
      }
      return reader.read().then(pump);
    };
    return reader.read().then(pump);
  },
  /**
   * Materiales IA (Lenguaje): genera + persiste SpecialText para un resource_type.
   * Reusa get_text/save_text (y generate_history para Audio Slider).
   * onProgress({ title, content }) se llama mientras llega el stream.
   */
  generateAndSaveReadingText({
    school,
    gradeCode,
    resourceTypeId,
    theme = "amistad y naturaleza",
    details = "Texto breve y claro para comprensión lectora en clase.",
    onProgress,
  } = {}) {
    const rt = Number(resourceTypeId) || 3;
    const age = Number(gradeCode || 0) + 4;
    const expectTitle = [1, 2, 3, 9].includes(rt);
    // type=4 → título entre <…> (igual que TextMessageComponent no-slider).
    const streamType = rt === 9 ? 3 : 4;
    return this.generateText(
      "",
      "",
      theme,
      "Narrador omnisciente",
      streamType,
      details,
      age,
      "",
      rt
    )
      .then((body) =>
        this.collectTextStream(body, { expectTitle, onProgress })
      )
      .then(({ title, content }) => {
        const safeTitle = title || theme;
        const safeContent =
          content ||
          "Había una vez un texto de lectura para trabajar en clase.";
        const payload = {
          title: safeTitle,
          text: safeContent,
          school,
          text_type: rt,
        };
        // Misma DB que generate_page_questions + create_material (worker).
        // saveText() en main deja el id huérfano al armar el PDF por ticket.
        if (rt === 9) {
          return this.generateHistory({
            title: safeTitle,
            text: safeContent,
            school,
          });
        }
        return withTicket("text", (ticket) =>
          planificationApiFor(ticket)
            .post("save_text/", payload)
            .then((response) => response.data)
        );
      });
  },
  // generateQuestion(payload) {
  //   return api.post('generate_question/', payload).then(response => response.data);
  // },
  // generateMathQuestion(payload) {
  //   return api.post('generate_math_question/', payload).then(response => response.data);
  // },
  generateMathQuestion(question, school) {
    const params = new URLSearchParams({
      type: question.type,
      age: question.age,
      difficulty: question.difficulty,
      school: school,
    });

    if (question.oa) {
      params.append("oa_id", question.oa.id);
      params.append("oa_name", question.oa.name);
      params.append("oa_description", question.oa.description);
    }
    if (question.subtag) {
      params.append("subtag_id", question.subtag.id);
      params.append("subtag_description", question.subtag.description);
    }

    return withTicket("math_question", (ticket) =>
      fetch(`${PLANIFICATIONS_BASE_URL}/generate_math_question/?${params}`, {
        headers: planificationFetchHeaders(ticket),
      }).then((response) => response)
    );
  },
  generateVisualMathQuestions(payload) {
    // Prek visual gen runs on main (local OpenAI key + NT/math stack).
    // Ticket/worker offload is for the remote planifications fleet; this
    // endpoint lives on the same Django process as Material para la clase.
    return api
      .post("generate_visual_math_questions/", payload)
      .then((response) => response.data);
  },
  planificarMaterialProgresion(payload) {
    return api
      .post("planificar_material_progresion/", payload)
      .then((response) => response.data);
  },
  generateGeneralQuestion(question, school) {
    const params = new URLSearchParams({
      type: question.type,
      age: question.age,
      difficulty: question.difficulty,
      school: school,
      subject: question.subject,
    });

    if (question.oa) {
      params.append("oa_id", question.oa.id);
      params.append("oa_name", question.oa.name);
      params.append("oa_description", question.oa.description);
    }
    if (question.subtag) {
      params.append("subtag_id", question.subtag.id);
      params.append("subtag_description", question.subtag.description);
    }

    return withTicket("general_question", (ticket) =>
      planificationApiFor(ticket)
        .get(`generate_general_question/?${params}`)
        .then((response) => response.data)
    );
  },
  generatePageQuestions(payload) {
    return withTicket("page_questions", (ticket) =>
      planificationApiFor(ticket)
        .post("generate_page_questions/", payload)
        .then((response) => response.data)
    );
  },
  generateQuestion(textId, question, school) {
    const params = new URLSearchParams({
      text_id: textId,
      type: question.type,
      age: question.age,
      difficulty: question.difficulty,
      school: school,
    });

    if (question.subtag) {
      params.append("subtag_id", question.subtag.id);
    } else if (question.oa) {
      params.append("oa_id", question.oa.id);
    }

    return withTicket("question", (ticket) =>
      planificationApiFor(ticket)
        .get(`generate_question/?${params}`)
        .then((response) => response.data)
    );
  },
  generateHistory(payload) {
    return withTicket("history", (ticket) =>
      planificationApiFor(ticket)
        .post("generate_history/", payload)
        .then((response) => response.data)
    );
  },
  getStudentsByGrade(schoolID, year, gradeCode) {
    return api
      .get(
        `schools/${schoolID}/get_students_by_grade/?grade_code=${gradeCode}&year=${year}`
      )
      .then((response) => response.data);
  },
  processStream(reader, decoder, page, vue_context) {
    return reader.read().then(function processChunk({ done, value }) {
      if (value) {
        let decoded = decoder.decode(value);
        if (decoded.includes("[complete:")) {
          // sacar el question y pushear
          let stringWithoutBrackets = decoded.slice(10, -1);
          const q = JSON.parse(stringWithoutBrackets);
          // page.questions[index] = q;
          page.questions.pop();
          page.questions.push(q);
        } else {
          let splitDecoded = decoded.split(";");
          let chunks = splitDecoded.slice(0, -1);
          let index = page.questions.findIndex((q) =>
            q.question.includes("Generando pregunta")
          );
          chunks.forEach((item) => {
            if (item.includes("ST ")) {
              let text = item.slice(3);
              page.questions[index].question += text;
            } else if (item.includes("OA ")) {
              let text = item.slice(3);
              page.questions[index].tag.name = text;
            } else if (item.includes("ALT ")) {
              let text = item.slice(4);
              if (text.includes("A)")) {
                text = text.slice(3);
                page.questions[index].alternatives[0].text += text;
              } else if (text.includes("B)")) {
                text = text.slice(3);
                page.questions[index].alternatives[1].text += text;
              } else if (text.includes("C)")) {
                text = text.slice(3);
                page.questions[index].alternatives[2].text += text;
              } else {
                text = text.slice(3);
                page.questions[index].alternatives[3].text += text;
              }
            }
          });
        }
      }
      if (done) {
        vue_context.$emit("tag-completed", vue_context.planification);
        return;
      }
      return reader.read().then(processChunk);
    });
  },
  deleteQuestionImage(question) {
    return api
      .post("delete_question_image/", question)
      .then((response) => response.data);
  },
  regenQuestionImage(question_id) {
    return api
      .post("regen_question_image/", question_id)
      .then((response) => response.data);
  },
  getSingleSlideOnline(payload) { //, csrftoken) {
    // csrftoken kept in the signature for backward compatibility with callers;
    // not used now that auth is via Bearer ticket against the worker server.
    return fetchPlanificationTicket("slide").then((ticket) =>
      fetch(
        `${PLANIFICATIONS_BASE_URL}/assisted_materials/get_single_slide_online/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${ticket}`,
          },
          body: payload,
        }
      ).then((response) => response.body)
    );
  },
  getSingleSlideImage(payload) {
    return fetchPlanificationTicket("slide_image").then((ticket) =>
      planificationApiFor(ticket)
        .post("assisted_materials/get_single_slide_image/", payload)
        .then((response) => response.data)
    );
  },
  downloadSlidesPPTX(payload) {
    return api
      .post("assisted_materials/download_slides_pptx/", payload)
      .then((response) => response.data);
  },
  // Question list + max scores for the manual score-input form.
  // Lightweight read — stays on main, not externalized.
  getMaterialScoringSheet(materialId) {
    return api
      .get(
        `assisted_materials/get_material_scoring_sheet/?material=${materialId}`
      )
      .then((response) => response.data);
  },
  // Render the "Informe Material" DOCX from manually-entered scores.
  // Externalized to pruebas; scope "report" → INFORMAL (materials_uses).
  generateMaterialReport(payload) {
    return withTicket("report", (ticket) =>
      planificationBlobFor(ticket)
        .post(
          "assisted_materials/create_material_report_from_scores/",
          payload
        )
        .then((response) => response)
    );
  },
};
